import { ComponentProps, forwardRef } from 'react'

import { clsx } from 'clsx'

import { SelectorIcon, XIcon } from '../icons/index.js'
import { useFloatingContext } from '../utils/floating/index.js'
import { useInputColors } from '../utils/styles/index.js'
import { useSearchableSelect } from './SearchableSelectContext.js'

export type SearchableSelectButtonProps = ComponentProps<'button'> & {
  dense?: boolean
  hideIcon?: boolean
  hideClearIcon?: boolean
  hideResetIcon?: boolean
}

export const SearchableSelectButton = forwardRef<
  HTMLButtonElement,
  SearchableSelectButtonProps
>((props, ref) => {
  const {
    children,
    className,
    placeholder,
    dense = false,
    hideIcon = false,
    hideClearIcon = false,
    hideResetIcon = false,
    ...rest
  } = props
  const {
    inputValue,
    disabled,
    invalid,
    // isOpen,
    getComboboxProps,
    getInputProps,
    getToggleButtonProps,
    selectedItem,
    reset,
  } = useSearchableSelect()
  const { reference } = useFloatingContext()
  const {
    backgroundClsx,
    borderClsx,
    invalidClsx,
    disabledClsx,
    iconClsx,
    placeholderClsx,
    focusClsx,
    fontClsx,
  } = useInputColors({
    invalid,
    disabled,
  })

  const clearButton = (
    <button
      type="button"
      className="absolute inset-y-0 end-0 flex items-center px-2 rounded-md"
      aria-label="clear selection"
      onClick={e => {
        e.stopPropagation()
        reset()
      }}
    >
      <XIcon className={clsx('w-5 h-5', iconClsx)} aria-hidden="true" />
    </button>
  )

  const toggleButton = (
    <span
      className="absolute inset-y-0 end-0 flex items-center pe-2 pointer-events-none"
      aria-label="toggle menu"
    >
      <SelectorIcon className={clsx('w-5 h-5', iconClsx)} aria-hidden="true" />
    </span>
  )

  const onKeyDown = event => {
    if (event.key === 'Backspace' && !inputValue) {
      reset()
    }
  }

  const getIcon = () => {
    if (dense || hideIcon) {
      return null
    }
    if (selectedItem) {
      if (hideClearIcon) {
        return null
      }
      return !hideResetIcon ? clearButton : null
    }
    return toggleButton
  }

  return (
    <div ref={reference}>
      <div
        className={clsx(
          'block w-full rounded-md group relative',
          'py-2 text-start cursor-default',
          [
            'appearance-none',
            'text-base',
            fontClsx,
            backgroundClsx,
            !dense && borderClsx,
            !dense && focusClsx,
            invalid && invalidClsx,
            disabled && disabledClsx,
          ],
          !dense && 'ps-3 pe-10',
          className,
        )}
        {...getComboboxProps(getToggleButtonProps({ disabled, ref, ...rest }))}
      >
        <span className="block truncate">
          <div className="flex space-s-2">
            {selectedItem && !inputValue && (
              <div className="min-w-0">{children}</div>
            )}
            <input
              className={clsx(
                'focus:outline-none bg-transparent flex-1 overflow-hidden',
                'text-base',
                fontClsx,
                placeholderClsx,
              )}
              {...getInputProps({
                placeholder: selectedItem ? '' : placeholder,
                disabled,
                onKeyDown,
              })}
            />
          </div>
        </span>
        {getIcon()}
      </div>
    </div>
  )
})
